import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

interface NavigationContextValue {
  /** True when there's at least one internal page we can go back to. */
  canGoBack: boolean;
  /** Navigate back by one entry in the internal stack. No-op if canGoBack is false. */
  goBack: () => void;
}

const NavigationContext = createContext<NavigationContextValue>({
  canGoBack: false,
  goBack: () => {},
});

/**
 * Tracks an internal navigation stack so we can show a "back" button that:
 *  - Only appears when the user has navigated internally at least once
 *  - Never backs out of the app to an external site (we only count PUSH
 *    transitions that happened while mounted inside this provider)
 *
 * Must be rendered inside a React Router context (below `<BrowserRouter>`).
 */
export function NavigationProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const stackRef = useRef<string[]>([location.pathname + location.search]);
  const [depth, setDepth] = useState(stackRef.current.length);

  useEffect(() => {
    const key = location.pathname + location.search;
    const stack = stackRef.current;

    if (navigationType === 'PUSH') {
      stack.push(key);
    } else if (navigationType === 'POP') {
      // Browser back/forward — trim our stack to match
      if (stack.length > 1) {
        stack.pop();
      }
    } else if (navigationType === 'REPLACE') {
      // Replace current entry
      stack[stack.length - 1] = key;
    }

    setDepth(stack.length);
  }, [location.pathname, location.search, navigationType]);

  const canGoBack = depth > 1;

  const goBack = useCallback(() => {
    if (stackRef.current.length > 1) {
      navigate(-1);
    }
  }, [navigate]);

  return (
    <NavigationContext.Provider value={{ canGoBack, goBack }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useInternalNavigation(): NavigationContextValue {
  return useContext(NavigationContext);
}
